import { toast } from "sonner";
import { useOnboardingState } from "@/hooks/useOnboardingState";
import {
  classifyAuthFlowError,
  handleSignupAuthFailure,
  isAuthFlowErrorRetryable,
  normalizeAuthError,
  runAuthCallbackWithRetries,
  runGoogleAuthWithRetries,
} from "@/lib/auth/onboarding-auth-errors";
import { authClient } from "@/lib/auth-client";
import { sanitizeAuthRedirect } from "@/lib/auth-redirect-client";
import { trackOnboarding } from "@/lib/telemetry";
import { useBotDetection } from "./useBotDetection";
import { useSignupForm } from "./useSignupForm";

export function useAccountCreation() {
  const { state, actions } = useOnboardingState();

  const {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    authMethod,
    setAuthMethod,
    isLoading,
    setIsLoading,
    magicLinkSent,
    setMagicLinkSent,
    isResending,
    setIsResending,
    isEmailValid,
    hasEmailError,
    isPasswordValid,
    hasPasswordError,
  } = useSignupForm();

  // Bot detection
  const {
    honeypotWebsite,
    setHoneypotWebsite,
    honeypotCompany,
    setHoneypotCompany,
    isBot,
    hasInteracted,
    formRenderTime,
  } = useBotDetection();

  const canSubmitMagicLink = isEmailValid && !isLoading;
  const canSubmitPassword = isEmailValid && isPasswordValid && !isLoading;
  const canSubmit = authMethod === "password" ? canSubmitPassword : canSubmitMagicLink;

  /**
   * Get the redirect URL after successful auth.
   * Checks onboarding state first, then localStorage fallback.
   * Returns the URL and clears the stored value.
   */
  const getRedirectUrl = (): string => {
    // Check onboarding state first
    let returnTo = state.returnTo;
    
    // Fall back to localStorage if not in state
    if (!returnTo) {
      try {
        returnTo = localStorage.getItem("aideco_auth_redirect");
      } catch {
        // localStorage unavailable
      }
    }
    
    // Clear both storage locations
    actions.setReturnTo(null);
    try {
      localStorage.removeItem("aideco_auth_redirect");
    } catch {
      // localStorage unavailable
    }
    
    // For preview browser-edit workflow: stay on current page if no returnTo set
    // This prevents redirecting to /onboarding/first-render when user is in preview workflow
    if (!returnTo) {
      const currentPath = window.location.pathname;
      if (currentPath.includes("preview-browser-edit")) {
        return currentPath;
      }
    }
    
    // Return the stored URL or default to onboarding
    return returnTo || "/onboarding/first-render";
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      actions.reset();
      // Check for stored return URL
      let callbackURL: string;
      try {
        const storedReturn = localStorage.getItem("aideco_auth_redirect");
        // For preview browser-edit workflow: stay on current page if no returnTo set
        const currentPath = window.location.pathname;
        const isPreviewEditPath = currentPath.includes("preview-browser-edit");
        const defaultPath = isPreviewEditPath ? currentPath : "/onboarding/first-render";
        callbackURL = storedReturn 
          ? `${window.location.origin}${storedReturn}`
          : `${window.location.origin}${defaultPath}`;
        // Note: we preserve localStorage value for other components that may need it
      } catch {
        // For preview browser-edit workflow: stay on current page
        const currentPath = window.location.pathname;
        const isPreviewEditPath = currentPath.includes("preview-browser-edit");
        const defaultPath = isPreviewEditPath ? currentPath : "/onboarding/first-render";
        callbackURL = `${window.location.origin}${defaultPath}`;
      }
      
      const ok = await runGoogleAuthWithRetries("signup", "google", async () => {
        await authClient.signIn.social({
          provider: "google",
          callbackURL,
        });
      });
      if (ok) {
        trackOnboarding.signupCompleted("google");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmitPassword) {
      return;
    }

    const botStatus = isBot();
    if (botStatus === "honeypot") {
      toast.error("Please try again.");
      return;
    }

    setIsLoading(true);

    try {
      const returnTo = getRedirectUrl();
      const callbackURL = `${window.location.origin}${returnTo}`;

      const { success, lastError } = await runAuthCallbackWithRetries(
        async () =>
          new Promise((resolve) => {
            authClient.signUp.email(
              {
                email,
                password,
                name: email.split("@")[0],
                callbackURL,
              },
              {
                onSuccess: () => resolve({ outcome: "success" as const }),
                onError: (err: unknown) => {
                  const normalized = normalizeAuthError(err);
                  const type = classifyAuthFlowError(normalized);
                  resolve({
                    outcome: "error" as const,
                    error: err,
                    retryable: isAuthFlowErrorRetryable(type, normalized.status),
                  });
                },
              },
            );
          }),
      );

      if (success) {
        trackOnboarding.signupCompleted("email");
        actions.closeModal();
        window.location.href = sanitizeAuthRedirect(returnTo, "/onboarding/first-render");
        return;
      }

      if (lastError !== undefined) {
        await handleSignupAuthFailure("email", lastError);
      }
    } catch (error) {
      await handleSignupAuthFailure("email", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      return;
    }

    const botStatus = isBot();
    if (botStatus === "honeypot") {
      toast.error("Please try again.");
      return;
    }

    setIsLoading(true);

    try {
      const returnTo = getRedirectUrl();
      const callbackURL = `${window.location.origin}${returnTo}`;

      const metadata = {
        formTimingMs: Date.now() - formRenderTime,
        hasInteracted,
      };

      const { success, lastError } = await runAuthCallbackWithRetries(
        async () =>
          new Promise((resolve) => {
            authClient.signIn.magicLink(
              {
                email,
                callbackURL,
                ...metadata,
              } as Parameters<typeof authClient.signIn.magicLink>[0],
              {
                onSuccess: () => resolve({ outcome: "success" as const }),
                onError: (err: unknown) => {
                  const normalized = normalizeAuthError(err);
                  const type = classifyAuthFlowError(normalized);
                  resolve({
                    outcome: "error" as const,
                    error: err,
                    retryable: isAuthFlowErrorRetryable(type, normalized.status),
                  });
                },
              },
            );
          }),
      );

      if (success) {
        trackOnboarding.signupCompleted("magic-link");
        setMagicLinkSent(true);
        toast.success("Check your email for the magic link!");
        return;
      }

      if (lastError !== undefined) {
        await handleSignupAuthFailure("magic-link", lastError);
      }
    } catch (error) {
      await handleSignupAuthFailure("magic-link", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendMagicLink = async () => {
    setIsResending(true);
    try {
      const returnTo = getRedirectUrl();
      const callbackURL = `${window.location.origin}${returnTo}`;
      
      const { success, lastError } = await runAuthCallbackWithRetries(
        async () =>
          new Promise((resolve) => {
            authClient.signIn.magicLink(
              { email, callbackURL },
              {
                onSuccess: () => resolve({ outcome: "success" as const }),
                onError: (err: unknown) => {
                  const normalized = normalizeAuthError(err);
                  const type = classifyAuthFlowError(normalized);
                  resolve({
                    outcome: "error" as const,
                    error: err,
                    retryable: isAuthFlowErrorRetryable(type, normalized.status),
                  });
                },
              },
            );
          }),
      );

      if (success) {
        toast.success("Magic link sent! Please check your inbox.");
        return;
      }

      if (lastError !== undefined) {
        await handleSignupAuthFailure("magic-link", lastError);
      }
    } catch (error) {
      await handleSignupAuthFailure("magic-link", error);
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToSignup = () => {
    setMagicLinkSent(false);
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    authMethod,
    setAuthMethod,
    isLoading,
    setIsLoading,
    magicLinkSent,
    setMagicLinkSent,
    isResending,
    setIsResending,
    isEmailValid,
    hasEmailError,
    isPasswordValid,
    hasPasswordError,

    honeypotWebsite,
    setHoneypotWebsite,
    honeypotCompany,
    setHoneypotCompany,

    canSubmit,
    handleGoogleSignIn,
    handlePasswordSignUp,
    handleEmailSignUp,
    handleResendMagicLink,
    handleBackToSignup,
    actions,
  };
}
